import React from "react"
import Select, { Props, components } from "react-select"

import { useMediaQuery } from "@hooks/useMediaQuery"
import { SelectOptionType } from "@mono/essentials/types/GlobalConfigTypes"

interface MonoSelectProps extends Props {
  onValueChange?: (newValue: SelectOptionType) => void
  hasError?: boolean
  pagination?: boolean
  selectedValueColor?: string
  isFromWidget?: boolean
  prefixIcon?: any
  containerBackground?: string
  biggerText?: boolean
  outLineSelect?: boolean
}

const MonoSelect = ({
  options,
  value,
  onValueChange,
  hasError,
  pagination,
  isFromWidget,
  prefixIcon,
  biggerText,
  outLineSelect = true,
  ...props
}: MonoSelectProps) => {
  const isMobile = useMediaQuery(768)
  const SingleValue = ({ children, ...props }: any) => {
    return (
      <components.SingleValue {...props}>
        {children}
        {pagination && " / page"}
      </components.SingleValue>
    )
  }

  const ValueContainer = ({ children, ...props }: any) => {
    return (
      components.ValueContainer && (
        <components.ValueContainer {...props}>
          {prefixIcon && <span className={`absolute left-2 size-5`}>{prefixIcon}</span>}
          {children}
        </components.ValueContainer>
      )
    )
  }

  return (
    <Select
      options={options}
      placeholder={props.placeholder || "Select"}
      components={{ SingleValue, ValueContainer }}
      onChange={(value) => {
        onValueChange?.(value as unknown as SelectOptionType)
      }}
      classNamePrefix={"mono-select"}
      closeMenuOnScroll={(e: Event) =>
        isMobile &&
        e?.target &&
        !(e?.target as HTMLElement)?.classList?.contains("mono-select__menu-list")
      }
      value={value}
      styles={{
        container: (baseStyles) => ({
          ...baseStyles,
          width: "100%",
        }),
        control: (baseStyles) => ({
          ...baseStyles,
          borderColor: hasError ? "red" : isFromWidget ? "#CCCCCC" : "rgb(231 231 232)",
          borderRadius: outLineSelect ? "6px" : "0px",
          boxShadow: "none !important",
          background: props.containerBackground || "#FFFFFF",
          borderWidth: outLineSelect ? "1px" : "0px",
        }),
        input: (baseStyles) => ({
          ...baseStyles,
          boxShadow: "none",
          input: {
            boxShadow: "none !important",
            fontSize: "14px",
            lineHeight: "120%",
          },
        }),
        menuPortal: (baseStyles) => ({
          ...baseStyles,
          zIndex: 10,
        }),
        menu: (baseStyles) => ({
          ...baseStyles,
          borderRadius: "6px",
          margin: "4px 0",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.04)",
          border: "1px solid rgb(231 231 232)",
        }),
        menuList: (base: any) => ({
          ...base,
          padding: "12px 8px 4px",

          "::-webkit-scrollbar": {
            width: "3px",
            borderRadius: "7px",
          },
          "::-webkit-scrollbar-track": {
            background: "#E7E7E8",
          },
          "::-webkit-scrollbar-thumb": {
            background: "#CCCDD0",
            borderRadius: "7px",
          },
          "::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
        dropdownIndicator: (base) => ({
          ...base,
          color: props.containerBackground && isFromWidget ? "#FFFFFF" : "#cccccc",
          "&:hover": {
            color: props.containerBackground && isFromWidget ? "#FFFFFF" : "#999999",
          },
        }),
        option: (baseStyles, state) => ({
          ...baseStyles,
          backgroundColor: state.isFocused
            ? "#F2F6FF"
            : state.isSelected
              ? "#F2F6FF"
              : baseStyles.backgroundColor,
          color: "#363741",
          borderRadius: "4px",
          cursor: "pointer",
          margin: "4px 0",
          fontSize: "14px",
          lineHeight: "120%",
          "&:hover": {
            backgroundColor: "#F2F6FF",
            color: "#363741",
          },
        }),
        singleValue: (baseStyles, state) => ({
          ...baseStyles,
          fontSize: "14px",
          lineHeight: "120%",
          color: props.selectedValueColor || "var(--color-primary)",
        }),
        valueContainer: (base) => ({
          ...base,
          padding: `${prefixIcon ? "0px 0px 0px 26px" : "0px 0px 0px 8px"}`,
        }),
        placeholder: (baseStyles, state) => ({
          ...baseStyles,
          fontSize: "14px",
          lineHeight: "120%",
        }),
      }}
      {...props}
    />
  )
}

export default MonoSelect
